/* eslint-disable react-hooks/exhaustive-deps */
import {FC, useContext, useState, useEffect, useMemo} from 'react'
import {useQuery} from 'react-query'
import {
  createResponseContext,
  initialQueryResponse,
  initialQueryState,
  PaginationState,
  QUERIES,
  stringifyRequestQuery,
  WithChildren,
} from '../../../../../_metronic/helpers'
import {getUserReports} from './_requests'
import {UserReports} from './_models'
import {useQueryRequest} from './QueryRequestProvider'

const QueryResponseContext = createResponseContext<UserReports>(initialQueryResponse)
const QueryResponseProvider: FC<WithChildren> = ({children}) => {
  const {state} = useQueryRequest()
  const [query, setQuery] = useState<string>(stringifyRequestQuery(state))
  const updatedQuery = useMemo(() => stringifyRequestQuery(state), [state])

  useEffect(() => {
    if (query !== updatedQuery) {
      setQuery(updatedQuery)
    }
  }, [updatedQuery])

  const {
    isFetching,
    refetch,
    data: response,
  } = useQuery(
    `${QUERIES.GROUP_LIST}-${query}`,
    () => {
      return getUserReports(query)
    },
    {cacheTime: 0, keepPreviousData: true, refetchOnWindowFocus: false}
  )

  return (
    <QueryResponseContext.Provider value={{isLoading: isFetching, refetch, response, query}}>
      {children}
    </QueryResponseContext.Provider>
  )
}

const useQueryResponse = () => useContext(QueryResponseContext)

const useQueryResponseData = () => {
  const {response} = useQueryResponse()
  if (!response) {
    return []
  }

  return response?.data || []
}

const useQueryTotalPage = () => {
  const {response} = useQueryResponse() as any
  if (!response) {
    return {}
  }
  return response?.totalPage || {}
}

const useQueryTotalsData = () => {
  const {response} = useQueryResponse() as any
  if (!response) {
    return {}
  }

  return response?.totals || {}
}

const useQueryFilterDatesData = () => {
  const {response} = useQueryResponse() as any
  if (!response) {
    return {}
  }

  return response?.filterDates || {}
}

const useQueryResponsePagination = () => {
  const defaultPaginationState: PaginationState = {
    links: [],
    ...initialQueryState,
  }

  const {response} = useQueryResponse()

  if (!response || !response.meta) {
    return defaultPaginationState
  }

  const pagination: PaginationState = {
    page: response.meta.current_page,
    per_page: response.meta.per_page,
    links: response.meta.links?.map((link) => {
      const pageMatch = String(link.url).match(/page=(\d+)/)
      const pageValue = pageMatch ? parseInt(pageMatch[1]) : null
      return {...link, page: pageValue}
    }),
    total: response.meta.total,
  }

  return pagination
}

const useQueryResponseLoading = (): boolean => {
  const {isLoading} = useQueryResponse()
  return isLoading
}

export {
  QueryResponseProvider,
  useQueryResponse,
  useQueryResponseData,
  useQueryResponsePagination,
  useQueryResponseLoading,
  useQueryTotalsData,
  useQueryFilterDatesData,
  useQueryTotalPage,
}
